
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-brand text-white hover:bg-blue-700",
        destructive:
          "bg-red text-white hover:bg-red/90",
        outline:
          "border border-brand text-brand bg-white hover:bg-blue-50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-brand underline-offset-4 hover:underline",
        gradient: "bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:opacity-90",
        glassy: "backdrop-blur-md bg-white/10 border border-white/20 hover:bg-white/20 text-foreground",
        cta: "bg-orange text-white font-bold hover:bg-orange/90",
        success: "bg-green text-white hover:bg-green/90",
        warning: "bg-yellow text-gray-dark hover:bg-yellow/90",
        danger: "bg-red text-white hover:bg-red/90",
        info: "bg-info text-white hover:bg-info/90",
      },
      size: {
        default: "h-10 px-6 py-3",
        sm: "h-9 rounded-xl px-3",
        lg: "h-11 rounded-xl px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
